export const DEFAULT_CLEARING_PARTY_ID = -1;

import {mapActions, mapGetters} from "vuex";
import * as types from "@/store/types";
import { isClient } from "@/utils.js";
import { SMA_TRADE_BUY_TAB, SMA_TRADE_SELL_TAB, TRADE_UNIT_CRYPTO_QUANTITY, TRADE_UNIT_USD_AMOUNT } from "./views/Consolidated Views/sma/dashboard/sma-constants";


export const AssetAccessMixin = {
    computed: {
        ...mapGetters({
            getAccountContext: "trade/" + types.TRADING_GETTER_ACCOUNT_CONTEXT,
            getAssetAccess: "trade/" + types.TRADE_GETTER_ASSET_ACCESS,
            userProfile: "main/" + types.MAIN_GETTER_USER_PROFILE,
        })
    },
    methods: {
        ...mapActions({
            loadAccountsContext: "trade/" + types.TRADING_LOAD_ACCOUNT_CONTEXT,
            loadAssetAccess: "trade/" + types.TRADING_LOAD_ASSET_ACCESS,
        }),

        _getAccessibleAssets(providerId, orgId, stateCodes, includeCustodialAssets) {
            const orgAssetAccess = this.getAssetAccess?.[providerId]?.[orgId];
            const fullAccess = orgAssetAccess?.full_access || [];
            const custodyOnly = includeCustodialAssets ? (orgAssetAccess?.custody_only || []) : [];
            const unsupported = orgAssetAccess?.unsupported || [];
            const stateExemptedAssets = unsupported.filter(asset =>
                asset.states && !asset.states.some(state => stateCodes.includes(state))
            );
            return [...fullAccess, ...custodyOnly, ...stateExemptedAssets];
        },

        async getAssets(providerId, accountIds, includeCustodialAssets = false) {
            if (!providerId) {
                console.warn("[getAssets] No providerId supplied — returning empty.");
                return [];
            }

            const advisorOrgId = isClient() ? null : (this.userProfile?.organization_id || null);
            const hasAccounts = accountIds && accountIds.length > 0;

            if (!hasAccounts && !advisorOrgId) {
                console.warn("[getAssets] No accountIds and no advisorOrgId supplied — returning empty.");
                return [];
            }

            // 1. Load account context (org ids + state codes) for all accounts
            if (hasAccounts) {
                await this.loadAccountsContext(accountIds);
            }

            // 2. Collect unique org ids across all accounts + advisor
            const accountOrgIds = hasAccounts
                ? accountIds.map(id => this.getAccountContext.accountOrgIds[id]).filter(orgId => orgId != null)
                : [];
            const orgIds = [...new Set([...accountOrgIds, advisorOrgId].filter(id => id != null))];

            if (orgIds.length === 0) {
                console.warn("[getAssets] No org ids resolved for accounts or advisor — returning empty.");
                return [];
            }

            // 3. Load asset access data for all relevant orgs
            const assetAccessParams = {
                provider_id: providerId,
                org_ids: orgIds,
            };
            await this.loadAssetAccess(assetAccessParams);

            // 4. Resolve assets per account (each with its own org + state codes), dedup by symbol
            let availableAssets = new Map(); // symbol -> asset object

            if (hasAccounts) {
                for (const accountId of accountIds) {
                    const orgId = this.getAccountContext.accountOrgIds[accountId] || null;
                    const stateCodes = this.getAccountContext.accountStateCodes[accountId] || [];

                    if (!orgId) {
                        console.warn(`[getAssets] Account ${accountId} has no org id — skipping.`);
                        continue;
                    }

                    const assets = this._getAccessibleAssets(providerId, orgId, stateCodes, includeCustodialAssets);
                    assets.forEach(a => availableAssets.set(a.symbol, a));
                }

                // 5. If advisor org is available, intersect with advisor-level access (no state filtering)
                if (advisorOrgId) {
                    const advisorAssets = new Map(this._getAccessibleAssets(providerId, advisorOrgId, [], includeCustodialAssets).map(a => [a.symbol, a]));
                    const intersected = new Map();
                    for (const [symbol, asset] of availableAssets) {
                        if (advisorAssets.has(symbol)) intersected.set(symbol, asset);
                    }
                    availableAssets = intersected;
                }
            } else {
                // No accounts — use advisor org only (no state filtering)
                const assets = this._getAccessibleAssets(providerId, advisorOrgId, [], includeCustodialAssets);
                assets.forEach(a => availableAssets.set(a.symbol, a));
            }

            return [...availableAssets.values()];
        },

        async coinIsTradeableOnProviderAndAccount(symbol, provider_id, accountId) {
            const tradeableAssets = await this.getAssets(provider_id, [accountId], false);
            return tradeableAssets.some(a => a.symbol === symbol);
        },
    }
}

export const CoinsByProvider = {
    // Extends AssetAccessMixin with multi-provider tracking and batch loading
    mixins: [AssetAccessMixin],
    data() {
        return {
            provider_ids: new Set()
        }
    },
    methods: {
        addProvider(provider_id) {
            this.provider_ids.add(provider_id)
        },
        addProviders(ids) {
            ids.forEach(provider_id => this.addProvider(provider_id))
        },
        async loadSymbols(accountIds) {
            await this.loadAccountsContext(accountIds);
            let results = []
            this.provider_ids.forEach(provider_id => {
                results.push(this.getAssets(provider_id, accountIds, true))
            })
        
            await Promise.all(results)
        },            
    }
}

export function accountCanAfford(account, purchaseSummary) {
    //buying crypto with USD
    if(purchaseSummary.tradeDirection === SMA_TRADE_BUY_TAB.id) {
        return accountHasAmount(account, "USD", purchaseSummary.assetPurchaseAmount);
    }
    //selling crypto by coin quantity
    else if(purchaseSummary.tradeDirection === SMA_TRADE_SELL_TAB.id 
        && purchaseSummary.tradeUnit === TRADE_UNIT_CRYPTO_QUANTITY.code) {
        return accountHasQuantity(account, purchaseSummary.selectedAsset, purchaseSummary.assetPurchaseQuantity);
    }
    //selling crypto by USD amount
    else if(purchaseSummary.tradeDirection === SMA_TRADE_SELL_TAB.id 
        && purchaseSummary.tradeUnit === TRADE_UNIT_USD_AMOUNT.code) {
        return accountHasAmount(account, purchaseSummary.selectedAsset, purchaseSummary.assetPurchaseAmount);
    }
    return false
}

function accountHasQuantity(account, symbol, quantity) {
    let asset = account.assets.find(asset => asset.symbol === symbol)
    return asset && asset.quantity >= quantity;
}
function accountHasAmount(account, symbol, amount) {
    let asset = account.assets.find(asset => asset.symbol === symbol)
    return asset && asset.mv >= amount;
}

export function getClearingPartyIdForCache(clearingPartyId) {
    return clearingPartyId === null || clearingPartyId === undefined ? DEFAULT_CLEARING_PARTY_ID
                                                                     : clearingPartyId;
}
